import { Debounce } from 'angular-debounce-throttle';
import { ApiService } from 'src/app/api.service';
import { ThemingService } from 'src/app/theming.service';
import { AppComponent } from 'src/app/app.component';
import { Component, OnInit } from '@angular/core';
import { ExportClientsService } from 'src/app/export-clients.service';
import { AuthService } from 'src/app/auth.service';
import { LambdaService } from 'src/app/lambda.service';
import { Router } from '@angular/router';

declare let $: any;

@Component({
  selector: 'app-consult-clients',
  templateUrl: './consult-clients.component.html',
  styleUrls: ['./consult-clients.component.scss']
})
export class ConsultClientsComponent implements OnInit {
  constructor(
    public appComponent: AppComponent,
    private apiService: ApiService,
    public themingService: ThemingService,
    private exportClientsService: ExportClientsService,
    private authService: AuthService,
    public lambdaService: LambdaService,
    private router: Router,
  ) {}

  client: any;
  searchString: string = '';
  saleInfo: any;
  dataStatus = 2;

  Math = Math;
  
  // Mapa para rastrear el beneficiario seleccionado por item: "saleId_itemId" -> beneficiaryId
  selectedBeneficiary: { [key: string]: number } = {};

  loading: boolean = false;
  modalDelete = false;
  permissions: any = {};
  usersAndBeneficiaries: any[] = [];
  groupedOrders: any[] = [];
  expandedTabs: { [key: string]: { conditions: boolean, beneficiaries: boolean } } = {};

  // Propiedades para paginación
  currentPage: number = 1;
  itemsPerPage: number = 6;
  totalPages: number = 0;
  paginatedOrders: any[] = [];

  // Propiedades para el feedback del botón
  itemLoading: { [key: string]: boolean } = {};
  itemMessages: { [key: string]: { type: 'success' | 'error', message: string } | null } = {};

  ngOnInit(): void {
    const shopSubscription = this.themingService.shopTheming[0].shopSubscription;
    if(shopSubscription.shopValidateClientAccess == 0){
      //redirect to home
      this.router.navigate(['/home']);
    }
    this.authService.getUserPermissions().then((permissions) => {
      this.permissions = permissions || {};
    });
  }

  @Debounce(500)
  serchClient() {
    this.getClient();
  }

  async getClient() {
    this.loading = true;
    this.groupedOrders = [];
    this.paginatedOrders = [];
    this.client = null;
    this.selectedBeneficiary = {};
    
    try {
      const data: any = await this.lambdaService.getClientByNationalId(
        this.themingService.shopId,
        this.searchString
      );
      
      this.client = data.data?.[0];
      this.dataStatus = this.client?.result === 0 ? 0 : 1;
      
      if (this.client && this.client.items) {
        this.groupOrdersBySaleId();
        this.groupItemsWithinOrders();
        this.initializeBeneficiarySelections();
        this.applyBeneficiaryInheritance(); // ⭐ NUEVO: Asegura herencia visual
      }
    } catch (error) {
      console.log(error);
      this.dataStatus = 0;
    }
    this.loading = false;
  }

  // Genera una clave única para evitar conflictos en ngModel dentro de *ngFor
  getItemKey(saleId: number, itemId: number): string {
    return `${saleId}_${itemId}`;
  }

  /**
   * Calcula sesiones disponibles dinámicamente según el beneficiario seleccionado
   * Se usa en el template para mostrar: disponible / total
   */
  getAvailableSessions(saleId: number, item: any): number {
    const key = this.getItemId(item);
    const selectedId = this.selectedBeneficiary[key] ?? 0; // 0 = Titular por defecto
    
    const ben = item.beneficiaries?.find((b: any) => b.beneficiaryId === selectedId);
    const used = ben ? (ben.bookingSessions || 0) : (item.titularBookingSessions || item.bookingSessions || 0);
    return Math.max(0, (item.itemSessions || 0) - used);
  }

  /**
   * Aplica herencia de beneficiarios: si un hijo no tiene, copia los del padre
   * y asegura que el selectedBeneficiary se sincronice
   */
  applyBeneficiaryInheritance(): void {
    if (!this.groupedOrders) return;
    
    this.groupedOrders.forEach(order => {
      order.items?.forEach((item: any) => {
        // Si es padre y tiene hijos
        if (item.children?.length && item.beneficiaries?.length) {
          item.children.forEach((child: any) => {
            // Si el hijo no tiene beneficiarios, heredar del padre
            if (!child.beneficiaries || child.beneficiaries.length === 0) {
              child.beneficiaries = JSON.parse(JSON.stringify(item.beneficiaries));
            }
            
            // Sincronizar la selección de beneficiario padre -> hijo
            const parentKey = this.getItemId(item);
            const childKey = this.getItemId(child);
            if (this.selectedBeneficiary[parentKey] !== undefined) {
              this.selectedBeneficiary[childKey] = this.selectedBeneficiary[parentKey];
            }
          });
        }
      });
    });
  }

  /**
   * Se ejecuta al cambiar el select del padre.
   * Sincroniza la selección con todos los hijos del paquete.
   */
  onBeneficiaryChange(saleId: number, parentItem: any, selectedId: number): void {
    const parentKey = this.getItemId(parentItem);
    this.selectedBeneficiary[parentKey] = selectedId;

    // Propagar a hijos
    if (parentItem.children?.length) {
      parentItem.children.forEach((child: any) => {
        const childKey = this.getItemId(child);
        this.selectedBeneficiary[childKey] = selectedId;
        
        // Si el hijo no tenía beneficiarios visibles, forzar herencia ahora
        if (!child.beneficiaries?.length && parentItem.beneficiaries?.length) {
          child.beneficiaries = JSON.parse(JSON.stringify(parentItem.beneficiaries));
        }
      });
    }
  }

  /**
   * Inicializa las selecciones de beneficiarios después de cargar los datos.
   * LLAMAR DESPUÉS DE: this.groupItemsWithinOrders(); en getClient()
   */
  initializeBeneficiarySelections(): void {
    if (!this.groupedOrders) return;
    
    this.groupedOrders.forEach(order => {
      order.items?.forEach((item: any) => {
        const key = this.getItemId(item);
        
        // Por defecto: Titular (0) o primer beneficiario disponible
        this.selectedBeneficiary[key] = item.beneficiaries?.[0]?.beneficiaryId ?? 0;
        
        // Inicializar también los hijos
        item.children?.forEach((child: any) => {
          const childKey = this.getItemId(child);
          this.selectedBeneficiary[childKey] = this.selectedBeneficiary[key];
        });
      });
    });
  }

  groupOrdersBySaleId() {
    const ordersMap = new Map();
    
    this.client.items.forEach((item: any) => {
      if (!ordersMap.has(item.saleId)) {
        ordersMap.set(item.saleId, {
          saleId: item.saleId,
          saleDate: item.saleDate || new Date(),
          items: []
        });
      }
      ordersMap.get(item.saleId).items.push(item);
    });

    // Convertir a array y ordenar por saleId (más alto primero)
    this.groupedOrders = Array.from(ordersMap.values()).sort((a, b) => {
      return Number(b.saleId) - Number(a.saleId); // Descendente
    });

    // Calcular total de páginas
    this.totalPages = Math.ceil(this.groupedOrders.length / this.itemsPerPage);
    
    // Aplicar paginación
    this.applyPagination();
  }

  applyPagination() {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedOrders = this.groupedOrders.slice(startIndex, endIndex);
  }

  groupItemsWithinOrders() {
    this.groupedOrders.forEach(order => {
      const parents: any[] = [];
      const children: any[] = [];

      // Separar padres e hijos
      order.items.forEach((item: any) => {
        if (item.packageProductId) {
          children.push({ ...item, assigned: false });
        } else {
          parents.push({ ...item, children: [] });
        }
      });

      const finalItems: any[] = [];

      // Asignar hijos a cada padre
      parents.forEach(parent => {
        // Buscar hijos que correspondan a este paquete
        const matchingChildren = children.filter(
          child =>
            !child.assigned &&
            child.packageProductId === parent.productId
        );

        // Asignar SOLO los necesarios (1 a 1 por repetición real)
        matchingChildren.forEach(child => {
          if (!child.assigned) {
            parent.children.push(child);
            child.assigned = true;
          }
        });

        finalItems.push(parent);
      });

      // Agregar hijos que no encontraron padre (edge case)
      const unassignedChildren = children.filter(c => !c.assigned);
      finalItems.push(...unassignedChildren);

      order.items = finalItems;
    });
  }

  formatMoney(amount: number, currency: string) {
    return currency == 'CRC' ? '₡'+amount : '$'+amount
  }

  changePage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.applyPagination();
    
    // Scroll suave al inicio de las órdenes
    setTimeout(() => {
      const ordersContainer = document.querySelector('.orders-container');
      if (ordersContainer) {
        ordersContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.changePage(this.currentPage + 1);
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.changePage(this.currentPage - 1);
    }
  }

  // Método para obtener array de páginas para mostrar
  getPagesArray(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  }

  getTimeElapsed(dateString: string): string {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    const days = diffDays % 30;
    
    let parts = [];
    if (years > 0) parts.push(`${years} ${years === 1 ? 'año' : 'años'}`);
    if (months > 0) parts.push(`${months} ${months === 1 ? 'mes' : 'meses'}`);
    if (days > 0) parts.push(`${days} ${days === 1 ? 'día' : 'días'}`);
    
    return parts.length > 0 ? `Hace ${parts.join(' ')}` : 'Hoy';
  }

  toggleTab(saleId: string, tab: 'conditions' | 'beneficiaries') {
    if (!this.expandedTabs[saleId]) {
      this.expandedTabs[saleId] = { conditions: false, beneficiaries: false };
    }
    this.expandedTabs[saleId][tab] = !this.expandedTabs[saleId][tab];
  }

  isTabExpanded(saleId: string, tab: 'conditions' | 'beneficiaries'): boolean {
    return this.expandedTabs[saleId] ? this.expandedTabs[saleId][tab] : false;
  }

  /**
   * Versión actualizada de discountSesion que maneja beneficiarios
   */
  async discountSesion(item: any) {
    const itemId = this.getItemId(item);
    const saleId = item.saleId;
    
    // Obtener el beneficiario seleccionado para este item
    const selectedBenId = this.selectedBeneficiary[itemId] ?? 0;
    // obtener el correo del beneficiario seleccionado (si existe)
    const selectedBen = item.beneficiaries?.find((b: any) => b.beneficiaryId === selectedBenId);
    const selectedBenEmail = selectedBen ? selectedBen.email : this.client.email;

    // Validación extra: no permitir si no hay sesiones disponibles para este beneficiario
    if (this.getAvailableSessions(saleId, item) <= 0) {
      this.itemMessages[itemId] = {
        type: 'error',
        message: 'No hay sesiones disponibles para este beneficiario'
      };
      setTimeout(() => { this.itemMessages[itemId] = null; }, 3000);
      return;
    }

    this.itemLoading[itemId] = true;
    this.itemMessages[itemId] = null;

    try {
      // ⚠️ Si tu lambda NO acepta beneficiaryId, elimina el último parámetro
      console.log('Llamando a discountItemSession con:', JSON.stringify({
        shopId: this.themingService.shopId,
        saleId: item.saleId,
        itemId: item.itemId,
        nationalId: this.client.nationalId,
        beneficiaryId: selectedBenId
      }));
      const data: any = await this.lambdaService.discountItemSession(
        this.themingService.shopId,
        item.saleId,
        item.itemId,
        this.client.nationalId,
        selectedBenId
      );

      console.log("Respuesta discountItemSession:", data);

      if (data && data.done === true) {
        // Éxito
        this.itemMessages[itemId] = {
          type: 'success',
          message: '¡Sesión descontada exitosamente!'
        };

        // Actualizar frontend: incrementar bookingSessions del beneficiario seleccionado
        if (this.client?.items) {
          const updatedItem = this.client.items.find((i: any) => 
            i.saleId === item.saleId && i.itemId === item.itemId
          );
          if (updatedItem?.beneficiaries) {
            const ben = updatedItem.beneficiaries.find((b: any) => b.beneficiaryId === selectedBenId);
            if (ben) {
              ben.bookingSessions = (ben.bookingSessions || 0) + 1;
            }
            // Fallback: si no hay beneficiarios específicos, actualizar el general
            if (!ben) {
              updatedItem.bookingSessions = (updatedItem.bookingSessions || 0) + 1;
            }
          } else {
            updatedItem.bookingSessions = (updatedItem.bookingSessions || 0) + 1;
          }
        }

        setTimeout(() => { this.itemMessages[itemId] = null; }, 3000);
      } else {
        throw new Error('La operación no se completó correctamente');
      }
    } catch (error) {
      console.log('Error al descontar sesión:', error);
      this.itemMessages[itemId] = {
        type: 'error',
        message: 'Error al descontar sesión. Intente nuevamente.'
      };
      setTimeout(() => { this.itemMessages[itemId] = null; }, 5000);
    } finally {
      this.itemLoading[itemId] = false;
    }
  }

  hasGracePeriod(item: any): boolean {
    // Si no tiene días de gracia, retorna false
    if (!item.productGracePeriod || item.productGracePeriod <= 0) {
      return false;
    }

    const saleDate = new Date(item.saleDate.replace(' ', 'T'));
    const today = new Date();

    // Diferencia en milisegundos
    const diffTime = today.getTime() - saleDate.getTime();
    // Convertir a días
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    // true = todavía está en gracia
    return diffDays < item.productGracePeriod;
  }

  // Método para validar si condiciones tiene contenido real
  hasValidConditions(conditions: string): boolean {
    if (!conditions) return false;
    const textContent = conditions.replace(/<[^>]*>/g, '').trim();
    return textContent.length > 0;
  }

  // Método para validar si hay beneficiarios
  hasValidBeneficiaries(beneficiaries: any[]): boolean {
    return Array.isArray(beneficiaries) && beneficiaries.length > 0;
  }

  // Método para validar si un item tiene al menos una sección válida
  hasValidSections(item: any): boolean {
    const hasConditions = this.hasValidConditions(item.productConditions);
    const hasBeneficiaries = this.hasValidBeneficiaries(item.beneficiaries);
    return hasConditions || hasBeneficiaries;
  }

  // Método para generar ID único del item
  getItemId(item: any): string {
    //console.log('Generando ID para item:', item, `- ${item.saleId}_${item.itemId}`);
    return `${item.saleId}_${item.itemId}`;
  }

  notVerified() {
    $('#errorClientsModal').modal('show');
  }

  closeModal() {
    $('#errorClientsModal').modal('hide');
    $('#deleteClientModal').modal('hide');
  }
}